// Read only information present in a bounded audio prefix. A format/quality
// label is never substituted for a missing frame or STREAMINFO field.
export type AudioProbe = {
  bitrate: number | null; sampleRate: number | null; bitsPerSample: number | null;
  channels: number | null; encodedDuration: number | null;
  bitrateBasis: 'mpeg-frame' | null;
  codec?: 'aac';
};

// BMFF containers may extend past the prefix. Read only complete child fields
// at their declared offsets; never scan media payload for an "mp4a" string.
function readMp4Aac(input: Uint8Array): Partial<AudioProbe> | null {
  const bytes = input.subarray(0, 1024), view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const ascii = (start: number, end: number) => String.fromCharCode(...bytes.subarray(start, end));
  const u32 = (offset: number) => view.getUint32(offset);
  const u64 = (offset: number) => u32(offset) <= 0x1fffff ? u32(offset) * 0x100000000 + u32(offset + 4) : null;
  type Box = { type: string; body: number; end: number; complete: boolean };
  let nodes = 0, invalid = false;
  const boxes = (start: number, end: number): Box[] => {
    const found: Box[] = [];
    for (let p = start; p + 8 <= Math.min(end, bytes.length);) {
      if (++nodes > 128) { invalid = true; break; }
      let length: number | null = u32(p), header = 8;
      if (length === 1) { if (p + 16 > bytes.length) break; length = u64(p + 8); header = 16; }
      if (length === 0) length = end - p;
      if (length === null || length < header || !Number.isSafeInteger(p + length) || p + length > end) { invalid = true; break; }
      found.push({ type: ascii(p + 4, p + 8), body: p + header, end: p + length, complete: p + length <= bytes.length });
      p += length;
    }
    return found;
  };
  const child = (parent: Box, type: string) => {
    const matches = boxes(parent.body, parent.end).filter(box => box.type === type);
    return matches.length === 1 ? matches[0] : undefined;
  };
  const descriptor = (start: number, end: number) => {
    if (start + 2 > end) return null;
    const tag = bytes[start++]; let length = 0;
    for (let n = 0; n < 4 && start < end; n++) {
      const value = bytes[start++]; length = length * 128 + (value & 127);
      if (!(value & 128)) return start + length <= end ? { tag, body: start, end: start + length } : null;
    }
    return null;
  };
  const roots = boxes(0, Number.MAX_SAFE_INTEGER), ftyp = roots[0];
  if (!ftyp || ftyp.type !== 'ftyp' || !ftyp.complete || ftyp.end - ftyp.body < 8) return null;
  let audioTracks = 0, measured: Partial<AudioProbe> | null = null;
  for (const moov of roots.filter(box => box.type === 'moov')) for (const trak of boxes(moov.body, moov.end).filter(box => box.type === 'trak')) {
    const mdia = child(trak, 'mdia'); if (!mdia) continue;
    const hdlr = child(mdia, 'hdlr');
    if (!hdlr?.complete || hdlr.end - hdlr.body < 12 || ascii(hdlr.body + 8, hdlr.body + 12) !== 'soun') continue;
    audioTracks++;
    const minf = child(mdia, 'minf'), stbl = minf && child(minf, 'stbl'), stsd = stbl && child(stbl, 'stsd');
    if (!stsd?.complete || stsd.end - stsd.body < 8 || u32(stsd.body + 4) !== 1) continue;
    const entries = boxes(stsd.body + 8, stsd.end), entry = entries[0];
    if (entries.length !== 1 || entry.type !== 'mp4a' || !entry.complete || entry.end - entry.body < 28 || view.getUint16(entry.body + 8) !== 0) continue;
    const extensions = boxes(entry.body + 28, entry.end), esds = extensions.find(box => box.type === 'esds');
    if (extensions.some(box => box.type === 'sinf') || !esds?.complete || esds.end - esds.body < 4) continue;
    const es = descriptor(esds.body + 4, esds.end);
    if (!es || es.tag !== 3 || es.end - es.body < 3) continue;
    const flags = bytes[es.body + 2]; let configOffset = es.body + 3;
    if (flags & 128) configOffset += 2;
    if (flags & 64) { if (configOffset >= es.end) continue; configOffset += 1 + bytes[configOffset]; }
    if (flags & 32) configOffset += 2;
    const config = descriptor(configOffset, es.end);
    if (!config || config.tag !== 4 || config.end - config.body < 13 || bytes[config.body] !== 0x40 || (bytes[config.body + 1] >> 2) !== 5) continue;
    const asc = descriptor(config.body + 13, config.end);
    if (!asc || asc.tag !== 5 || asc.end - asc.body < 2) continue;
    const objectType = bytes[asc.body] >> 3, rateIndex = ((bytes[asc.body] & 7) << 1) | (bytes[asc.body + 1] >> 7), channelConfig = (bytes[asc.body + 1] >> 3) & 15;
    const rate = [96000,88200,64000,48000,44100,32000,24000,22050,16000,12000,11025,8000,7350][rateIndex];
    const channels = [0,1,2,3,4,5,6,8][channelConfig];
    // Basic AAC-LC only. PCE, explicit frequencies, SBR/PS and non-default
    // GASpecificConfig need their own parser rather than guessed parameters.
    if (objectType !== 2 || !rate || !channels || (bytes[asc.body + 1] & 7) !== 0) continue;
    if (asc.end - asc.body >= 5) {
      const extension = (bytes[asc.body + 2] << 16) | (bytes[asc.body + 3] << 8) | bytes[asc.body + 4];
      if ((extension >> 13) === 0x2b7 && ((extension >> 8) & 31) === 5 && (extension & 128)) continue;
    }
    if (u32(entry.body + 24) / 65536 !== rate || view.getUint16(entry.body + 16) !== channels) continue;
    let encodedDuration: number | null = null;
    const mdhd = child(mdia, 'mdhd');
    if (mdhd?.complete) {
      const version = bytes[mdhd.body], required = version === 0 ? 20 : version === 1 ? 32 : 0;
      if (required && mdhd.end - mdhd.body >= required) {
        const timescale = u32(mdhd.body + (version === 0 ? 12 : 20)), duration = version === 0 ? u32(mdhd.body + 16) : u64(mdhd.body + 24);
        if (timescale > 0 && duration !== null && duration > 0 && duration / timescale <= 604800) encodedDuration = duration / timescale;
      }
    }
    measured = { codec: 'aac', sampleRate: rate, channels, encodedDuration };
  }
  return !invalid && audioTracks === 1 ? measured : null;
}

export function readAudioProbe(bytes: Uint8Array): AudioProbe {
  const result: AudioProbe = { bitrate: null, sampleRate: null, bitsPerSample: null, channels: null, encodedDuration: null, bitrateBasis: null };
  const ascii = (start: number, end: number) => String.fromCharCode(...bytes.subarray(start, end));
  if (bytes.length >= 42 && ascii(0, 4) === 'fLaC' && (bytes[4] & 0x7f) === 0 && ((bytes[5] << 16) | (bytes[6] << 8) | bytes[7]) === 34) {
    const rate = (bytes[18] << 12) | (bytes[19] << 4) | (bytes[20] >> 4);
    const depth = (((bytes[20] & 1) << 4) | (bytes[21] >> 4)) + 1;
    const samples = (bytes[21] & 15) * 0x100000000 + bytes[22] * 0x1000000 + bytes[23] * 0x10000 + bytes[24] * 0x100 + bytes[25];
    if (rate > 0 && rate <= 655350 && depth >= 4 && depth <= 32) {
      result.sampleRate = rate; result.bitsPerSample = depth; result.channels = ((bytes[20] >> 1) & 7) + 1;
      if (samples > 0) result.encodedDuration = samples / rate;
    }
    return result;
  }
  if (bytes.length >= 16 && ascii(4, 8) === 'ftyp') {
    Object.assign(result, readMp4Aac(bytes));
    return result;
  }
  let start = 0;
  if (bytes.length >= 10 && ascii(0, 3) === 'ID3') {
    if ([6, 7, 8, 9].some(index => bytes[index] & 0x80)) return result;
    start = 10 + (bytes[6] << 21) + (bytes[7] << 14) + (bytes[8] << 7) + bytes[9] + (bytes[5] & 0x10 ? 10 : 0);
  } else if (bytes.length < 4 || bytes[0] !== 0xff || (bytes[1] & 0xe0) !== 0xe0) return result;
  // MPEG bitrates are frame measurements, not a VBR file average.
  const mpeg1 = {
    3: [0,32,64,96,128,160,192,224,256,288,320,352,384,416,448],
    2: [0,32,48,56,64,80,96,112,128,160,192,224,256,320,384],
    1: [0,32,40,48,56,64,80,96,112,128,160,192,224,256,320],
  };
  const lower = { 3: [0,32,48,56,64,80,96,112,128,144,160,176,192,224,256], 2: [0,8,16,24,32,40,48,56,64,80,96,112,128,144,160], 1: [0,8,16,24,32,40,48,56,64,80,96,112,128,144,160] };
  for (let index = start; index + 4 <= bytes.length; index++) {
    if (bytes[index] !== 0xff || (bytes[index + 1] & 0xe0) !== 0xe0) continue;
    const version = (bytes[index + 1] >> 3) & 3, layer = (bytes[index + 1] >> 1) & 3;
    const rateIndex = (bytes[index + 2] >> 2) & 3, bitrateIndex = bytes[index + 2] >> 4;
    if (version === 1 || !layer || rateIndex === 3 || !bitrateIndex || bitrateIndex === 15) continue;
    const table = version === 3 ? mpeg1 : lower;
    result.bitrate = table[layer as 1 | 2 | 3][bitrateIndex] * 1000;
    result.bitrateBasis = 'mpeg-frame';
    result.sampleRate = [44100, 48000, 32000][rateIndex] / (version === 3 ? 1 : version === 2 ? 2 : 4);
    result.channels = bytes[index + 3] >> 6 === 3 ? 1 : 2;
    return result;
  }
  return result;
}
